/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  View,
  type GestureResponderEvent,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { getColor } from '@airasia-phoenix/react-native/utils/utils';
import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight';
import { FooterInsetContext } from './FooterInsetContext';
import { SheetHeader } from './SheetHeader';
import type {
  FlightsBottomSheetDismissReason,
  FlightsBottomSheetViewProps,
} from './types';

// A drag on the header or handle longer than this moves between snap points
// or closes the sheet.
const DRAG_THRESHOLD = 60;

// Converts a snap point ('90%' or a number of pixels) to a sheet height,
// bounded by the visible viewport once it is known.
const resolveSnapHeight = (
  snap: string | number,
  viewportHeight: number | undefined,
) => {
  const fraction =
    typeof snap === 'string' ? Number.parseFloat(snap) / 100 : undefined;
  if (viewportHeight === undefined)
    return typeof snap === 'number'
      ? snap
      : (`${Math.min(100, Math.max(0, (fraction || 0.9) * 100))}%` as `${number}%`);
  return Math.min(
    viewportHeight,
    Math.max(
      0,
      typeof snap === 'number' ? snap : viewportHeight * (fraction || 0.9),
    ),
  );
};

// The native sheet's floating footer uses layout-viewport coordinates. On web,
// keep the whole sheet inside the visible viewport and reserve footer space.
export const FlightsBottomSheetView = ({
  visible,
  onDismiss,
  children,
  title,
  onClose,
  closeButtonAccessibilityLabel,
  header,
  footer,
  showFooterDivider = true,
  footerStyle,
  scrollable = true,
  bodyStyle,
  snapPoints = ['90%'],
  showBackdrop = true,
  dismissOnBackdropPress = true,
  enablePanDownToClose = true,
  enableDynamicSizing = false,
  showHandle = false,
  testID = 'flights-bottom-sheet',
}: FlightsBottomSheetViewProps) => {
  const [presented, setPresented] = useState(visible);
  const [snapIndex, setSnapIndex] = useState(0);
  const presentedRef = useRef(visible);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const viewportHeight = useVisualViewportHeight(visible);
  const [viewportTop, setViewportTop] = useState(0);
  const dragStart = useRef(0);

  const dismiss = useCallback((reason: FlightsBottomSheetDismissReason) => {
    if (!presentedRef.current) return;
    presentedRef.current = false;
    setPresented(false);
    onDismissRef.current(reason);
  }, []);

  useEffect(() => {
    if (visible) {
      presentedRef.current = true;
      setPresented(true);
      setSnapIndex(0);
    } else {
      dismiss('programmatic');
    }
  }, [visible, dismiss]);

  useEffect(() => {
    if (
      !presented ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    )
      return;
    const viewport = window.visualViewport;
    const update = () => {
      if (!viewport || viewport.scale === 1)
        setViewportTop(viewport?.offsetTop || 0);
    };
    update();
    viewport?.addEventListener('scroll', update);
    viewport?.addEventListener('resize', update);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      viewport?.removeEventListener('scroll', update);
      viewport?.removeEventListener('resize', update);
      document.body.style.overflow = previousOverflow;
    };
  }, [presented]);

  // Opens at the first snap point; dragging the header or handle up moves to
  // the next one, dragging down moves back and then closes.
  const lastSnapIndex = Math.max(0, snapPoints.length - 1);
  const currentSnapIndex = Math.min(snapIndex, lastSnapIndex);
  const sheetHeight = resolveSnapHeight(
    snapPoints[currentSnapIndex] ?? '90%',
    viewportHeight,
  );
  const hasFooter = footer != null && footer !== false;
  const hasHeader = header != null || title != null || onClose != null;
  const panHandlers = {
    onStartShouldSetResponder: () => enablePanDownToClose || lastSnapIndex > 0,
    onResponderGrant: (event: GestureResponderEvent) => {
      dragStart.current = event.nativeEvent.pageY;
    },
    onResponderRelease: (event: GestureResponderEvent) => {
      const distance = event.nativeEvent.pageY - dragStart.current;
      if (distance < -DRAG_THRESHOLD && currentSnapIndex < lastSnapIndex) {
        setSnapIndex(currentSnapIndex + 1);
      } else if (distance > DRAG_THRESHOLD) {
        if (currentSnapIndex > 0) setSnapIndex(currentSnapIndex - 1);
        else if (enablePanDownToClose) dismiss('swipe');
      }
    },
  };
  // With dynamic sizing the body keeps its content height and only shrinks
  // (and scrolls) once the sheet reaches its snap height.
  const bodyLayout = enableDynamicSizing ? styles.dynamicBody : styles.body;

  return (
    <Modal
      transparent
      visible={presented}
      animationType="none"
      onRequestClose={() => dismiss('close-button')}
    >
      <View
        testID={`${testID}-viewport`}
        style={[
          styles.viewport,
          { top: viewportTop, height: viewportHeight ?? '100%' },
        ]}
        pointerEvents="box-none"
      >
        {showBackdrop ? (
          <Pressable
            testID={`${testID}-backdrop`}
            accessible={false}
            style={styles.backdrop}
            disabled={!dismissOnBackdropPress}
            onPress={() => dismiss('backdrop')}
          />
        ) : null}
        <View
          testID={testID}
          style={[
            styles.sheet,
            enableDynamicSizing
              ? { maxHeight: sheetHeight }
              : { height: sheetHeight },
          ]}
          accessibilityViewIsModal
        >
          {showHandle ? (
            <View
              testID={`${testID}-handle`}
              style={styles.handleContainer}
              {...panHandlers}
            >
              <View style={styles.handle} />
            </View>
          ) : null}
          {hasHeader ? (
            <View style={styles.header} {...panHandlers}>
              {header ??
                (title != null || onClose != null ? (
                  <SheetHeader
                    title={title}
                    testID={testID}
                    onClosePress={
                      onClose
                        ? () => {
                            onClose();
                            dismiss('close-button');
                          }
                        : undefined
                    }
                    closeButtonAccessibilityLabel={
                      closeButtonAccessibilityLabel
                    }
                  />
                ) : null)}
            </View>
          ) : null}
          <FooterInsetContext.Provider value={0}>
            {scrollable ? (
              <ScrollView
                testID={`${testID}-scroll`}
                style={[bodyLayout, bodyStyle]}
                keyboardShouldPersistTaps="handled"
              >
                {children}
              </ScrollView>
            ) : (
              <View
                testID={`${testID}-body`}
                style={[bodyLayout, styles.bodyContainer, bodyStyle]}
              >
                {children}
              </View>
            )}
          </FooterInsetContext.Provider>
          {hasFooter ? (
            <View
              testID={`${testID}-footer`}
              style={[
                styles.footer,
                showFooterDivider && styles.divider,
                footerStyle,
              ]}
            >
              {footer}
            </View>
          ) : null}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create((pd) => ({
  viewport: {
    position: 'absolute',
    left: 0,
    right: 0,
    justifyContent: 'flex-end',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: getColor(pd.sys.colour.overlay),
  },
  sheet: {
    width: '100%',
    maxHeight: '100%',
    minHeight: 0,
    backgroundColor: getColor(pd.sys.colour.bg_primary),
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
  },
  header: {
    flexShrink: 0,
    backgroundColor: getColor(pd.sys.colour.bg_primary),
  },
  handleContainer: {
    flexShrink: 0,
    alignItems: 'center',
    paddingBottom: pd.ref.space.spacing20.px,
    backgroundColor: getColor(pd.comp.bottom_sheet.container.colour),
  },
  handle: {
    marginTop: pd.ref.space.spacing40.px,
    width: 43,
    height: 3,
    backgroundColor: getColor(pd.comp.bottom_sheet.handle.colour),
  },
  body: { flex: 1, minHeight: 0 },
  dynamicBody: { flexGrow: 0, flexShrink: 1, minHeight: 0 },
  bodyContainer: { overflow: 'hidden' },
  footer: {
    flexShrink: 0,
    backgroundColor: getColor(pd.sys.colour.bg_primary),
    padding: 16,
    paddingBottom: 24,
    _web: {
      paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
    },
  },
  divider: {
    borderTopWidth: 1,
    borderTopColor: getColor(pd.sys.colour.outline_low),
  },
}));
